<template>
  <li class="flex gap-4" @mouseenter.prevent="() => (hovered = true)" @mouseleave.prevent="() => (hovered = false)">
    <div class="w-8 h-8">
      <AvatarComponent :name="comment.author" :avatarUrl="comment.authorAvatar" />
    </div>

    <div v-if="!editMode" class="flex flex-col flex-grow gap-1">
      <div class="flex justify-between">
        <span class="text-sm font-bold dark:text-zinc-400 truncate sm:max-w-[55ch] 2xl:max-w-[86ch]">{{ comment.author }}</span>
        <span class="text-xs text-slate-800 dark:text-zinc-400">{{ comment.createdAtS }}</span>
      </div>

      <div class="relative">
        <div class="text-sm break-all dark:text-zinc-300">
          <div class="comment-message" v-text="comment.content" />
          <span v-if="commentsStore.isCommentEdited(comment.id)" class="text-xs text-slate-800 dark:text-zinc-400">(edited)</span>
        </div>

        <div
          v-if="showEditActions"
          class="comment-edit-actions flex absolute right-0 top-0 bg-white dark:bg-zinc-900"
          :class="comment.commentable ? 'w-22' : 'w-10'"
        >
          <Button
            v-if="comment.commentable"
            :data-testid="`viewer-comment-${comment.id}-edit`"
            icon="general.pencil"
            type="link"
            variant="black"
            @click.prevent="enableEdit()"
          />
          <Button :data-testid="`viewer-comment-${comment.id}-remove`" icon="general.trash" type="link" variant="black" @click.prevent="removeComment()" />
        </div>
      </div>
      <span class="text-xs dark:text-zinc-300" v-if="comment.anonymous"> posted as anonymous </span>
    </div>

    <!-- EDIT COMMENT -->
    <div v-if="editMode" class="flex flex-col flex-grow gap-1 group">
      <div class="flex gap-4 mb-1">
        <span class="text-sm font-bold dark:text-zinc-400">Editing comment</span>
        <span @click.prevent="disableEdit()" class="text-sm font-bold cursor-pointer" :class="isDirty ? 'text-red-400' : 'text-blue-500'">
          {{ isDirty ? 'Cancel & discard changes' : 'Cancel' }}
        </span>
      </div>
      <div class="relative">
        <MessageInput
          ref="messageField"
          :hideLabel="true"
          :mention-enabled="true"
          :modelValue="comment.content"
          @submit="updateComment"
          @keydown.escape="disableEdit"
          @dirty="dirtyChange"
        />
      </div>
    </div>
  </li>
</template>
<script lang="ts" setup>
import { ref, Ref, computed } from 'vue';
import AvatarComponent from './Avatar.vue';
import { Comment, useCommentsStore } from '@/modules/viewer/store/useCommentsStore';
import { useConfirmModal } from '@/storybook/modal/useModal';

import Button from '@/storybook/button/Button.vue';
import MessageInput from './MessageInput.vue';

const props = defineProps<{ comment: Comment }>();

const commentsStore = useCommentsStore();
const hovered: Ref<boolean> = ref(false);
const editMode: Ref<boolean> = ref(false);
const isDirty = ref(false);

const showEditActions = computed(() => !!(props.comment.editable && hovered.value));

const { open: openRemoveModal } = useConfirmModal({
  title: 'Delete this comment?',
  message: 'This cannot be undone.',
  confirmText: 'Delete',
  cancelText: 'Cancel',
});

const { open: openDiscardModal } = useConfirmModal({
  title: 'Cancel & discard changes?',
  confirmText: 'Discard changes',
  cancelText: 'Back to editing',
});

const dirtyChange = (dirty: boolean) => {
  isDirty.value = dirty;
};

const enableEdit = () => {
  editMode.value = true;
};

const parseCommentBreaks = (comment: string) => comment.replace(/\n/g, '<br />');

const disableEdit = () => {
  if (isDirty.value) {
    return openDiscardModal().then((confirmed: boolean) => {
      if (confirmed) {
        editMode.value = false;
      }
    });
  }

  editMode.value = false;
};

const updateComment = (message: string) =>
  commentsStore.update(props.comment.id, message).then(() => {
    isDirty.value = false;
    editMode.value = false;
  });

const removeComment = () =>
  openRemoveModal().then((confirmed: boolean) => {
    confirmed && commentsStore.remove(props.comment.id);
  });
</script>
